/**
 * Web crash reporting — Sentry, errors only.
 *
 * Contract (enforced by test/e2e-crash-report.mjs):
 *  - ZERO network at rest: no sessions, no tracing, no replay, no client
 *    reports. The public /demo page must stay silent (docs/DEMO.md law).
 *  - Errors (render crashes via ErrorBoundary, window.onerror,
 *    unhandledrejection) ship one envelope to the shared Sentry project
 *    (same project as mobile; environment tag 'web' separates the streams —
 *    split into its own project later by swapping VITE_SENTRY_DSN).
 *  - Silent no-op when the DSN is absent or emulators are on.
 */
import * as Sentry from '@sentry/react';

export function initCrashReporting(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn || import.meta.env.VITE_USE_EMULATORS === 'true') return;

  Sentry.init({
    dsn,
    release: __BUILD_SHA__,
    environment: `web-${import.meta.env.MODE}`,
    // Errors only — strip everything that phones home without an error.
    defaultIntegrations: false,
    integrations: [
      Sentry.inboundFiltersIntegration(),
      Sentry.functionToStringIntegration(),
      Sentry.dedupeIntegration(),
      Sentry.globalHandlersIntegration({ onerror: true, onunhandledrejection: true }),
      Sentry.httpContextIntegration(),
      Sentry.linkedErrorsIntegration(),
      Sentry.breadcrumbsIntegration(),
    ],
    sendClientReports: false,
    tracesSampleRate: 0,
  });
}

/** ErrorBoundary hook — keeps Sentry out of component imports. */
export function reportCrash(error: unknown, componentStack?: string | null): void {
  Sentry.captureException(error, {
    contexts: componentStack ? { react: { componentStack } } : undefined,
  });
}
